import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendURL } from '../../utils/backendURL';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Calendar, Mail, Phone, Building2, Globe, User, Briefcase,
  Users, MessageSquare, Eye, X, RefreshCw, ExternalLink,
  CheckCircle, Clock, AlertCircle, Trash2, Filter, TrendingUp,
  Sparkles, MessageCircle
} from 'lucide-react';

const STATUSES = ['pending', 'contacted', 'scheduled', 'completed', 'cancelled'];

const SOURCE_META = {
  'funnel-book-demo': { label: 'Book Demo', color: 'bg-purple-100 text-purple-800', icon: Calendar },
  'funnel-talk-to-human': { label: 'Talk to Human', color: 'bg-pink-100 text-pink-800', icon: MessageCircle },
  organic: { label: 'Organic', color: 'bg-slate-100 text-slate-700', icon: Sparkles },
};

const STATUS_META = {
  pending: { color: 'bg-amber-100 text-amber-800', icon: Clock },
  contacted: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  scheduled: { color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
  completed: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  cancelled: { color: 'bg-rose-100 text-rose-800', icon: X },
};

const DemoLeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const BACKEND_URL = getBackendURL();
  const token = localStorage.getItem('admin_token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSource) params.append('source', filterSource);
      params.append('limit', '200');

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/demo-requests/?${params.toString()}`,
        { headers: authHeader }
      );
      setLeads(response.data || []);
    } catch (e) {
      console.error('Failed to fetch leads:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/demo-requests/stats`,
        { headers: authHeader }
      );
      setStats(response.data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStats();
    // eslint-disable-next-line
  }, [filterStatus, filterSource]);

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      setUpdating(true);
      const payload = { status: newStatus };
      if (adminNotes.trim() && adminNotes !== (selected.notes || '')) {
        payload.notes = adminNotes.trim();
      }
      if (newStatus === 'contacted' && !selected.contacted_at) {
        payload.contacted_at = new Date().toISOString();
      }
      const response = await axios.patch(
        `${BACKEND_URL}/api/admin/demo-requests/${selected.id}`,
        payload,
        { headers: authHeader }
      );
      setSelected(response.data);
      await Promise.all([fetchLeads(), fetchStats()]);
    } catch (e) {
      console.error('Failed to update lead:', e);
      alert('Update failed. Check console.');
    } finally {
      setUpdating(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      setUpdating(true);
      const response = await axios.patch(
        `${BACKEND_URL}/api/admin/demo-requests/${selected.id}`,
        { notes: adminNotes.trim() || null },
        { headers: authHeader }
      );
      setSelected(response.data);
      await fetchLeads();
    } catch (e) {
      console.error('Failed to save notes:', e);
    } finally {
      setUpdating(false);
    }
  };

  const deleteLead = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete lead from ${selected.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/demo-requests/${selected.id}`,
        { headers: authHeader }
      );
      setSelected(null);
      await Promise.all([fetchLeads(), fetchStats()]);
    } catch (e) {
      console.error('Failed to delete lead:', e);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return d;
    }
  };

  const getSourceMeta = (lead) => {
    if (lead.source && SOURCE_META[lead.source]) return SOURCE_META[lead.source];
    return SOURCE_META.organic;
  };

  return (
    <div className="p-6 space-y-6" data-testid="demo-leads-management">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-purple-600" />
            Demo / Leads
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            All leads captured from <b>/book-demo</b> and <b>/talk-to-human</b> funnels (plus organic landing-page demo requests).
          </p>
        </div>
        <Button
          onClick={() => { fetchLeads(); fetchStats(); }}
          variant="outline"
          data-testid="refresh-leads-btn"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="leads-stats">
          <StatCard label="Total Leads" value={stats.total} icon={Users} color="from-purple-500 to-blue-500" />
          <StatCard label="Last 7 days" value={stats.last_7_days} icon={TrendingUp} color="from-emerald-500 to-green-500" />
          <StatCard label="Pending" value={stats.by_status.pending} icon={Clock} color="from-amber-500 to-orange-500" />
          <StatCard label="Scheduled" value={stats.by_status.scheduled} icon={Calendar} color="from-indigo-500 to-blue-500" />
          <StatCard label="Book Demo" value={stats.by_source['funnel-book-demo']} icon={Calendar} color="from-purple-500 to-pink-500" />
          <StatCard label="Talk to Human" value={stats.by_source['funnel-talk-to-human']} icon={MessageCircle} color="from-pink-500 to-rose-500" />
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <Filter className="h-4 w-4" /> Filter:
          </div>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm bg-white"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            data-testid="filter-source"
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm bg-white"
          >
            <option value="">All sources</option>
            <option value="funnel-book-demo">Book Demo funnel</option>
            <option value="funnel-talk-to-human">Talk to Human funnel</option>
          </select>
          {(filterStatus || filterSource) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilterStatus(''); setFilterSource(''); }}
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          )}
          <span className="ml-auto text-sm text-slate-500">
            Showing <b className="text-slate-900">{leads.length}</b> lead{leads.length === 1 ? '' : 's'}
          </span>
        </div>
      </Card>

      {/* Leads table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Name / Company</th>
                <th className="px-4 py-3">Email / Phone</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading leads...</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No leads match this filter. New submissions from <b>/book-demo</b> and <b>/talk-to-human</b> will appear here.
                </td></tr>
              )}
              {!loading && leads.map((lead) => {
                const SrcIcon = getSourceMeta(lead).icon;
                const StatusIcon = (STATUS_META[lead.status] || STATUS_META.pending).icon;
                return (
                  <tr key={lead.id} className="hover:bg-slate-50 transition" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{lead.name}</div>
                      <div className="text-xs text-slate-500">{lead.company || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{lead.email}</div>
                      {lead.phone && <div className="text-xs text-slate-500">{lead.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${getSourceMeta(lead).color} gap-1`}>
                        <SrcIcon className="h-3 w-3" />
                        {getSourceMeta(lead).label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${(STATUS_META[lead.status] || STATUS_META.pending).color} gap-1 capitalize`}>
                        <StatusIcon className="h-3 w-3" />
                        {lead.status || 'pending'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelected(lead); setAdminNotes(lead.notes || ''); }}
                        data-testid={`view-lead-${lead.id}`}
                        className="gap-1"
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="lead-detail-drawer">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setSelected(null)} />
          <div className="ml-auto w-full max-w-2xl bg-white shadow-2xl overflow-y-auto relative">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Badge className={`${getSourceMeta(selected).color} gap-1`}>
                    {React.createElement(getSourceMeta(selected).icon, { className: 'h-3 w-3' })}
                    {getSourceMeta(selected).label}
                  </Badge>
                  <span>·</span>
                  <span>{formatDate(selected.created_at)}</span>
                </div>
                <h2 className="mt-1 text-xl font-bold text-slate-900">{selected.name}</h2>
                <a href={`mailto:${selected.email}`} className="text-sm text-purple-600 hover:underline inline-flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" /> {selected.email}
                </a>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="rounded-full p-2 hover:bg-slate-100"
                data-testid="close-drawer-btn"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Status updater */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => {
                    const StatusIcon = STATUS_META[s].icon;
                    const isCurrent = selected.status === s;
                    return (
                      <Button
                        key={s}
                        size="sm"
                        variant={isCurrent ? 'default' : 'outline'}
                        disabled={updating || isCurrent}
                        onClick={() => updateStatus(s)}
                        data-testid={`status-btn-${s}`}
                        className={`gap-1 capitalize ${isCurrent ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                      >
                        <StatusIcon className="h-3.5 w-3.5" />
                        {s}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Contact info */}
              <div className="grid sm:grid-cols-2 gap-3">
                <DetailField icon={Building2} label="Company" value={selected.company} />
                <DetailField icon={User} label="Role" value={selected.role} />
                <DetailField icon={Users} label="Team size" value={selected.team_size} />
                <DetailField icon={Briefcase} label="Monthly test volume" value={selected.monthly_test_volume} />
                <DetailField icon={Phone} label="Phone" value={selected.phone} />
                <DetailField icon={Calendar} label="Preferred time" value={selected.preferred_date} />
              </div>

              {/* App URL */}
              {selected.app_url && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5" /> App URL
                  </div>
                  <a
                    href={selected.app_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:underline inline-flex items-center gap-1 break-all"
                  >
                    {selected.app_url}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Message / pain */}
              {selected.message && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {selected.source === 'funnel-talk-to-human' ? 'Their question' : 'Biggest QA pain'}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              {/* UTM tracking */}
              {(selected.utm_source || selected.utm_campaign) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1.5">
                    UTM Tracking
                  </div>
                  <div className="flex flex-wrap gap-3 text-slate-700">
                    {selected.utm_source && <span><b>Source:</b> {selected.utm_source}</span>}
                    {selected.utm_campaign && <span><b>Campaign:</b> {selected.utm_campaign}</span>}
                  </div>
                </div>
              )}

              {/* Admin notes */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Internal notes</div>
                <Textarea
                  data-testid="admin-notes-textarea"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes for your team (call summary, follow-up date, etc.)"
                  rows={4}
                  className="text-sm"
                />
                <div className="mt-2 flex gap-2">
                  <Button onClick={saveNotes} disabled={updating} size="sm" data-testid="save-notes-btn">
                    {updating ? 'Saving...' : 'Save notes'}
                  </Button>
                  {selected.contacted_at && (
                    <span className="text-xs text-slate-500 inline-flex items-center">
                      Last contacted: {formatDate(selected.contacted_at)}
                    </span>
                  )}
                </div>
              </div>

              {/* Quick actions */}
              <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2">
                <a href={`mailto:${selected.email}`} className="inline-flex">
                  <Button variant="outline" size="sm" data-testid="email-lead-btn" className="gap-1">
                    <Mail className="h-3.5 w-3.5" /> Email
                  </Button>
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="inline-flex">
                    <Button variant="outline" size="sm" data-testid="call-lead-btn" className="gap-1">
                      <Phone className="h-3.5 w-3.5" /> Call
                    </Button>
                  </a>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deleteLead}
                  data-testid="delete-lead-btn"
                  className="gap-1 ml-auto text-rose-600 hover:bg-rose-50 border-rose-200"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{value ?? 0}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-sm`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </Card>
);

const DetailField = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="text-sm text-slate-800">{value || '—'}</div>
  </div>
);

export default DemoLeadsManagement;
